import { FileDownloadStatusEnum } from '../enums/FileDownloadStatusEnum';
import type ProcessStatus from '../interfaces/ProcessStatus';
import type { TransferService } from '../services/transferApiService';
import {
  getPendingConversion,
  hasPendingConversion,
  removePendingConversion,
} from './pdfConversionTracking';
import {
  coercePdfConversionFileId,
  coercePdfConversionFolderId,
  type PdfConversionTerminalOutcome,
} from './pdfConversionTerminal';

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_DURATION_MS = 5 * 60 * 1000;

const pollingIntervals = new Map<string, number>();
const pollingStartedAt = new Map<string, number>();
/** Consecutive Success polls that still lack newFileId — wait briefly for WS. */
const successWithoutFileIdCounts = new Map<string, number>();
const SUCCESS_WITHOUT_ID_GRACE_POLLS = 3;

let transferService: TransferService | null = null;
let transferServicePromise: Promise<TransferService> | null = null;
let onTerminalOutcome: ((outcome: PdfConversionTerminalOutcome) => void) | null = null;

const getTransferService = async (): Promise<TransferService> => {
  if (transferService) {
    return transferService;
  }

  if (!transferServicePromise) {
    transferServicePromise = import('../services/transferApiService').then((module) => {
      transferService = new module.TransferService();
      return transferService;
    });
  }

  return transferServicePromise;
};

const TERMINAL_STATUSES = new Set<FileDownloadStatusEnum>([
  FileDownloadStatusEnum.Success,
  FileDownloadStatusEnum.Failed,
  FileDownloadStatusEnum.PartialSuccess,
  FileDownloadStatusEnum.IntegrityCheckFailed,
]);

function parseProcessStatusExtras(response: ProcessStatus): {
  newFileId: number | null;
  destinationFolderId: number | null | undefined;
} {
  const loose = response as ProcessStatus & Record<string, unknown>;
  let newFileId = coercePdfConversionFileId(
    loose.newFileId ??
      loose.new_file_id ??
      loose.convertedFileId ??
      loose.converted_file_id ??
      loose.resultFileId ??
      loose.result_file_id,
  );
  let destinationFolderId = coercePdfConversionFolderId(
    loose.destinationFolderId ?? loose.destination_folder_id,
  );

  const rawAdditional = response.additionalData;
  if (typeof rawAdditional === 'string' && rawAdditional.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(rawAdditional) as Record<string, unknown>;
      if (newFileId == null) {
        newFileId = coercePdfConversionFileId(
          parsed.newFileId ??
            parsed.new_file_id ??
            parsed.convertedFileId ??
            parsed.converted_file_id ??
            parsed.resultFileId ??
            parsed.result_file_id,
        );
      }
      if (destinationFolderId === undefined) {
        destinationFolderId = coercePdfConversionFolderId(
          parsed.destinationFolderId ?? parsed.destination_folder_id,
        );
      }
    } catch {
      // ponytail: additionalData may be plain error text
    }
  }

  return { newFileId, destinationFolderId };
}

function mapProcessStatusToOutcome(
  processId: string,
  response: ProcessStatus,
  pendingDestinationFolderId: number | null | undefined,
  sourceFileId?: number,
  sourceFileName?: string,
): PdfConversionTerminalOutcome | null {
  if (!TERMINAL_STATUSES.has(response.status)) {
    return null;
  }

  if (response.status === FileDownloadStatusEnum.Success) {
    const extras = parseProcessStatusExtras(response);
    if (extras.newFileId == null && sourceFileId != null) {
      const loose = response as ProcessStatus & Record<string, unknown>;
      const maybeNew = coercePdfConversionFileId(loose.fileId ?? loose.file_id);
      if (maybeNew != null && maybeNew !== sourceFileId) {
        extras.newFileId = maybeNew;
      }
    }
    if (extras.newFileId == null) {
      // Poll often beats WS; wait a few intervals for new_file_id before toasting.
      const seen = (successWithoutFileIdCounts.get(processId) ?? 0) + 1;
      successWithoutFileIdCounts.set(processId, seen);
      if (seen < SUCCESS_WITHOUT_ID_GRACE_POLLS) {
        return null;
      }
    }
    successWithoutFileIdCounts.delete(processId);
    return {
      kind: 'success',
      processId,
      newFileName: response.name,
      sourceFileName,
      sourceFileId,
      destinationFolderId:
        extras.destinationFolderId !== undefined
          ? extras.destinationFolderId
          : (pendingDestinationFolderId ?? null),
      newFileId: extras.newFileId,
    };
  }

  const lastError =
    response.failedFilesList?.[0]?.lastError ?? response.additionalData ?? undefined;

  return {
    kind: 'failed',
    processId,
    errorCode: lastError,
    errorMessage: lastError,
  };
}

export function registerPdfConversionTerminalOutcomeHandler(
  handler: (outcome: PdfConversionTerminalOutcome) => void,
): () => void {
  onTerminalOutcome = handler;
  return () => {
    if (onTerminalOutcome === handler) {
      onTerminalOutcome = null;
    }
  };
}

export function stopPdfConversionPolling(processId: string): void {
  const intervalId = pollingIntervals.get(processId);
  if (intervalId != null) {
    window.clearInterval(intervalId);
    pollingIntervals.delete(processId);
  }
  pollingStartedAt.delete(processId);
  successWithoutFileIdCounts.delete(processId);
}

export async function pollPdfConversionOnce(processId: string): Promise<void> {
  if (!hasPendingConversion(processId)) {
    stopPdfConversionPolling(processId);
    return;
  }

  const startedAt = pollingStartedAt.get(processId) ?? getPendingConversion(processId)?.startedAt;
  if (startedAt != null && Date.now() - startedAt >= MAX_POLL_DURATION_MS) {
    removePendingConversion(processId);
    stopPdfConversionPolling(processId);
    onTerminalOutcome?.({ kind: 'timeout', processId });
    return;
  }

  try {
    const service = await getTransferService();
    const response = (await service.getProcessStatus(processId)) as ProcessStatus | undefined;
    if (!response || !hasPendingConversion(processId)) {
      stopPdfConversionPolling(processId);
      return;
    }

    const pending = getPendingConversion(processId);
    const outcome = mapProcessStatusToOutcome(
      processId,
      { ...response, processId },
      pending?.destinationFolderId,
      pending?.fileId,
      pending?.sourceFileName,
    );
    if (!outcome) {
      return;
    }

    removePendingConversion(processId);
    stopPdfConversionPolling(processId);
    onTerminalOutcome?.(outcome);
  } catch {
    // ponytail: ignore transient poll errors until timeout
  }
}

export function startPdfConversionPolling(processId: string): void {
  if (pollingIntervals.has(processId)) {
    return;
  }

  const pending = getPendingConversion(processId);
  pollingStartedAt.set(processId, pending?.startedAt ?? Date.now());

  void pollPdfConversionOnce(processId);
  const intervalId = window.setInterval(() => {
    void pollPdfConversionOnce(processId);
  }, POLL_INTERVAL_MS);
  pollingIntervals.set(processId, intervalId);
}

export function stopAllPdfConversionPolling(): void {
  pollingIntervals.forEach((intervalId) => window.clearInterval(intervalId));
  pollingIntervals.clear();
  pollingStartedAt.clear();
  successWithoutFileIdCounts.clear();
}

export function setPdfConversionTransferServiceForTests(service: TransferService): void {
  transferService = service;
  transferServicePromise = Promise.resolve(service);
}

export function resetPdfConversionPollingForTests(): void {
  stopAllPdfConversionPolling();
  transferService = null;
  transferServicePromise = null;
  onTerminalOutcome = null;
}

export const PDF_CONVERSION_POLL_INTERVAL_MS = POLL_INTERVAL_MS;
export const PDF_CONVERSION_MAX_POLL_DURATION_MS = MAX_POLL_DURATION_MS;
